import { useEffect, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import { StylesConfig } from "react-select";
import { useQueryClient } from "@tanstack/react-query";
import { FilterCriteria, useReactSelectTheme } from "@bagel-web/components";
import { CommonBagelAPIKey, Page } from "../types";
import { apiKeyQueries } from "../queries";
import { PaginatedSearch } from "../util/pagination";

export type fetchAPIKeysCallbackOptionalOrganization = (
  paginatedSearch: PaginatedSearch<{ notes?: string; organization?: string }>
) => Promise<Page<CommonBagelAPIKey>>;

export type APIKeySelectProps = {
  fetchAPIKeys: fetchAPIKeysCallbackOptionalOrganization;
  value: string;
  onChange: (e: { target: { value: string } }) => void;
  organization?: string;
  placeholder?: string;
  disabled?: boolean;
  localCriteria?: FilterCriteria<string>;
  /**
   * Called when `value` names a key that is provably not in the caller's
   * key list, so a caller persisting the id (the Playground keeps it in
   * localStorage) can drop a key that has since been revoked.
   *
   * Deliberately conservative, because a false positive deletes a live
   * selection while a false negative just leaves a dead id that fails at
   * request time. It fires only when all three hold: the load was
   * unfiltered (a search that filters the key out of view proves
   * nothing), the response was the complete list rather than a first page
   * of several, and `value` is still what it was when the request went
   * out. An org with more keys than fit one page therefore keeps a
   * revoked id until the user reselects; fixing that needs a lookup by
   * id, which `CommonAPI` does not expose today.
   */
  onValueUnavailable?: (value: string) => void;
};

const styleOverrides = (hasMore: boolean): StylesConfig => ({
  container: (baseStyles) => ({
    ...baseStyles,
    minWidth: "400px",
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    fontFamily: "monospace",
    fontSize: ".875em",
    color: state.isSelected ? "white" : "var(--bs-code-color)",
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    fontFamily: "monospace",
    fontSize: ".875em",
    color: "var(--bs-code-color)",
  }),
  menuList: (baseStyles) => ({
    ...baseStyles,
    ":after": hasMore
      ? {
          content: "'Narrow your search above for more options.'",
          color: "grey",
          textAlign: "center",
          display: "block",
          margin: "auto",
          padding: "5px",
        }
      : {},
  }),
});

const APIKeySelect = ({
  fetchAPIKeys,
  value,
  onChange,
  organization,
  placeholder,
  disabled = false,
  onValueUnavailable,
}: APIKeySelectProps) => {
  const queryClient = useQueryClient();
  const [hasMore, setHasMore] = useState(false);
  // Read inside the async callback so a response that landed after the
  // user picked something else cannot clear the newer selection.
  const currentValue = useRef(value);
  useEffect(() => {
    currentValue.current = value;
  }, [value]);
  const [cachedOptions, setCachedOptions] = useState<
    Array<{
      label: string;
      value: string;
    }>
  >([]);

  const loadOptions = (
    inputValue: string,
    callback: (options: Array<{ label: string; value: string }>) => void
  ) => {
    const fetchOptions = async () => {
      const apiKeys = await queryClient.fetchQuery({
        queryKey: apiKeyQueries.list({
          startsAfter: null,
          notes: inputValue,
          organization,
        }),
        queryFn: () => {
          return fetchAPIKeys({
            startsAfter: null,
            notes: inputValue,
            organization,
          });
        },
        staleTime: 30 * 1000,
      });
      const options = apiKeys.items.map((apiKey) => {
        return {
          value: apiKey.id,
          label: apiKey.notes
            ? `${apiKey.redacted_key} (${apiKey.notes})`
            : apiKey.redacted_key,
        };
      });
      setCachedOptions(options);
      setHasMore(apiKeys.next_page_starting_after !== null);
      const isCompleteList = apiKeys.next_page_starting_after === null;
      if (
        inputValue === "" &&
        isCompleteList &&
        value &&
        currentValue.current === value &&
        !options.some((option) => option.value === value)
      ) {
        onValueUnavailable?.(value);
      }
      callback(options);
    };

    fetchOptions();
  };

  return (
    <AsyncSelect
      isDisabled={disabled}
      value={cachedOptions.find((option) => option.value === value) ?? null}
      styles={styleOverrides(hasMore)}
      theme={useReactSelectTheme()}
      // Manage the cache ourselves so that we can correctly update the value of showMore.
      // TODO(daniel): When the search is cleared, loadOptions is NOT called, so we are not
      // correctly updating the value of showMore. Unfortunately AsyncSelect has no callback
      // to let us know when the inputValue is cleared.
      cacheOptions={false}
      loadOptions={loadOptions}
      placeholder={placeholder || "Type to search..."}
      isClearable={true}
      defaultOptions
      onChange={(newValue) => {
        const agentId = (newValue as { value: string })?.value;
        onChange({ target: { value: agentId } });
      }}
    />
  );
};

export default APIKeySelect;
